"use client";

import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { useModal } from "@/hooks/useModal";
import { type ProductSizes } from "@/lib/centra/formatters/sizes";
import { centraAdapter } from "@/lib/gtm/data-layer";
import type { BaseSelection } from "@frend-digital/centra-types/selection";
import { useBuyBox } from "@frend-digital/centra/client";
import { useIntersectionObserver } from "@frend-digital/ui/hooks";
import { useQueryParams } from "@frend-digital/ui/next";
import { useTranslations } from "next-intl";
import { Suspense, useEffect } from "react";
import styles from "./index.module.css";
import { useStickyProductStore } from "./StickyProduct/store";

const useBuyBoxLabels = () => {
	const t = useTranslations("product");

	return {
		add_to_cart: t("addToCart"),
		coming_soon: t("notify"),
		out_of_stock: t("notify"),
		select_size: t("select"),
	};
};

const UnsuspendedAddToCartButton = ({
	className,
	sizes,
}: {
	className?: string;
	sizes?: ProductSizes;
}) => {
	const t = useTranslations("product");
	const size = useQueryParams().get("size");
	const { open } = useModal("cart");

	const [ref, entry] = useIntersectionObserver<HTMLButtonElement>({
		threshold: 0.5,
	});
	const { setShow } = useStickyProductStore();

	useEffect(() => {
		// Skip first render
		if (!entry) return;
		setShow(!entry.isIntersecting);
	}, [entry, setShow]);

	const { getButtonLabel, getButtonProps } = useBuyBox({
		labels: useBuyBoxLabels(),
		sizes: sizes,
		// @ts-ignore
		onSuccess: ({ selection }, variables) => {
			open();

			centraAdapter.addToCart(selection as BaseSelection, variables.itemId);
		},
	});

	return (
		<Button ref={ref} {...getButtonProps()} className={className}>
			<Text className={styles.buttonText}>{getButtonLabel()}</Text>
		</Button>
	);
};

export const AddToCartButton: typeof UnsuspendedAddToCartButton = (props) => {
	const t = useTranslations("product");
	return (
		<Suspense
			fallback={
				<Button isLoading color="yellow" className={props.className}>
					{t("addToCart")}
				</Button>
			}>
			<UnsuspendedAddToCartButton {...props} />
		</Suspense>
	);
};
